import { Button } from "~/ui/button"
import { UserAvatar } from "~/users/user-avatar"
import { Check, ChevronLeft } from "lucide-react"
import { Link } from "react-router-dom"
import { UserProfileFragment, ContactProfileFragment } from "~/__generated__/graphql"

interface ProfileHeaderProps {
  user: UserProfileFragment
  isOwnProfile: boolean
  contact?: ContactProfileFragment | null
  onEditProfile?: () => void
  onAddToContacts?: () => void
  addToContactsLoading?: boolean
  onRemoveContact?: () => void
  removeContactLoading?: boolean
}

export const ProfileHeader = ({
  user,
  isOwnProfile,
  contact,
  onEditProfile,
  onAddToContacts,
  addToContactsLoading = false,
  onRemoveContact,
  removeContactLoading = false,
}: ProfileHeaderProps) => {
  return (
    <>
      {/* Full-width gradient header */}
      <div className="relative mb-8 h-32 bg-gradient-to-r from-blue-400 to-purple-600">
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2">
          <UserAvatar
            user={user}
            alt={`${user.fullName}'s profile picture`}
            className="h-16 w-16 border-4 border-white bg-white"
          />
        </div>
      </div>

      {/* Back to Contacts link and Remove Contact link */}
      <div className="mx-auto mb-4 flex w-full items-center justify-between px-4">
        <Link
          to="/contacts"
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
        >
          <ChevronLeft className="h-4 w-4" />
          Back To Contacts
        </Link>

        {/* Remove Contact link on the right */}
        {contact && onRemoveContact && (
          <button
            onClick={onRemoveContact}
            disabled={removeContactLoading}
            className="text-sm text-gray-600 underline hover:text-gray-800 disabled:opacity-50"
          >
            {removeContactLoading ? "Removing..." : "Remove Contact"}
          </button>
        )}
      </div>

      <div className="mx-auto max-w-2xl px-4">
        {/* Profile Info */}
        <div className="text-center">
          <h1 className="mb-1 text-2xl font-bold text-gray-900">{user.fullName}</h1>
          <p className="mb-4 text-gray-600">@{user.handle}</p>

          {/* Bio text */}
          {user.bio && <p className="mb-6 whitespace-pre-wrap text-gray-600">{user.bio}</p>}

          {/* Action Button */}
          {isOwnProfile ? (
            <Button onClick={onEditProfile} className="mb-6">
              Edit Profile
            </Button>
          ) : (
            <div className="mb-6">
              {user.isContact ? (
                <Button disabled>
                  In Your Contacts
                  <Check />
                </Button>
              ) : (
                <Button onClick={onAddToContacts} disabled={addToContactsLoading}>
                  {addToContactsLoading ? "Adding to Contacts..." : "Add to Contacts"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
